/**
 * The rules over the sky (M3-05) — how far you can see, what you can light,
 * and what the weather does to a wound.
 *
 * `weather.ts` holds what the sky *is* and `light.ts` holds what a burning
 * thing *is*; both depend on nothing, because `world.ts` has to name their
 * types in order to store them. Everything that reads world state lives here,
 * which is the same split `mind.ts`/`beasts.ts` makes.
 *
 * The load-bearing claim of the whole ticket is in `sightRadius` below:
 * **visibility is decided on the server**. A client is never sent an actor it
 * could not see, so there is no wallhack to write and no dark to turn off in a
 * config file. Night is dangerous because the server says the wolf is not
 * there, not because the renderer painted over it.
 */
import { capabilityFault } from './affordance.js';
import { chebyshev } from './actor.js';
import { parseKindle } from './actions.js';
import { deriveCapabilities } from './body.js';
import { isChannelling, isCommitted, isHelpless } from './fighter.js';
import { advanceInfection, rollInfections } from './infection.js';
import { carriedRadius, createLight, FUEL_TAG, hearthLightAt, isBurning, litAt, TORCH_RADIUS, } from './light.js';
import { isRelieved } from './needs.js';
import { hasLineOfSight } from './senses.js';
import { removeItem } from './item.js';
import { gameTime, TICKS_PER_GAME_HOUR } from './time.js';
import { advanceWeather, createWeather, daylight, infectionEnvironmentOf, kindleChance, phaseText, skyPhase, weatherText, wetnessOf, WEATHER_SIGHT, } from './weather.js';
/* -------------------------------------------------------------------------- *
 * How far you can see
 * -------------------------------------------------------------------------- */
/**
 * Sight at noon, in tiles.
 *
 * Chosen against the area of interest rather than pulled out of the air. M0-04
 * streams a 64×48 rect around the viewer, so a Chebyshev radius of 32 covers
 * every corner of it: **in clear daylight this ticket takes nothing away**, the
 * area of interest is still the only thing culling entities, and the eight-tile
 * margin that stops actors popping into view still does its job. What changes
 * is what happens when the light goes.
 *
 * The sim cannot import the protocol's `AOI_SIZE` — the arrow points the other
 * way — so this is the one number the two packages have to agree on by hand.
 * The snapshot's own tests are where that agreement is checked.
 */
export const DAY_SIGHT = 32;
/**
 * Sight at midnight with no light, in tiles.
 *
 * Three, and it is meant to frighten you. Wolves close at eight (M2-05) and
 * decide at four, so a man on the moor at midnight without a brand meets the
 * pack after it has already committed. That is the acceptance criterion stated
 * as a number.
 */
export const NIGHT_SIGHT = 3;
/** Nobody is ever blind: one tile is arm's length, and you always have that. */
export const MIN_SIGHT = 1;
/**
 * How far this actor can see, in tiles.
 *
 * Light is composed with `max` rather than added, and the reason is the whole
 * feel of light: a torch or nearby fire is not a bonus, it is a *floor*. It
 * buys you nothing at noon and it is everything at midnight, which is exactly
 * what light is worth to a person.
 */
export function sightRadius(input) {
    const ambient = NIGHT_SIGHT + (DAY_SIGHT - NIGHT_SIGHT) * clamp01(input.daylight);
    const weathered = ambient * WEATHER_SIGHT[input.weather];
    // Weather takes the same bite out of torchlight it takes out of daylight —
    // rain and fog are between your eye and the thing either way.
    const lit = Math.max(weathered, input.light * WEATHER_SIGHT[input.weather]);
    return Math.max(MIN_SIGHT, Math.round(lit * clamp01(input.vision)));
}
function clamp01(value) {
    return value < 0 ? 0 : value > 1 ? 1 : value;
}
/**
 * A world that predates M3-05 has no sky in it. `hydrateWorld` fills one in on
 * load, but the projection (M0-04) is also called with hand-built worlds in
 * tests, and a snapshot that threw on a missing field would make this ticket a
 * breaking change to every one of them.
 */
export function weatherOf(world) {
    return world.weather ?? createWeather();
}
export function lightsOf(world) {
    return world.lights ?? {};
}
/** What one actor can currently see, in tiles — the number the wire is culled by. */
export function sightRadiusOf(world, catalogue, actorId, tick) {
    const weather = weatherOf(world);
    // Optional, like `weatherOf` above: the projection this feeds is called with
    // hand-built worlds in tests, and a world with no bodies in it still has a sky.
    const combatant = world.combat?.[actorId];
    const lights = lightsOf(world);
    const actor = world.actors?.[actorId];
    // A fire is light for the person standing in its ring, not merely something
    // that makes them easier for others to spot. `litAt` is already the light it
    // has left at this exact tile, so the same fading ember cannot disagree about
    // warmth, visibility, and who it reveals.
    const nearbyLight = actor
        ? Math.max(litAt(lights, actor.x, actor.y, tick), world.map ? hearthLightAt(world.map, actor.x, actor.y) : 0)
        : 0;
    return sightRadius({
        daylight: daylight(gameTime(tick)),
        weather: weather.kind,
        light: Math.max(carriedRadius(lights, actorId, tick), nearbyLight),
        vision: visionOf(catalogue, combatant),
    });
}
/** The eyes an actor has left. 1 for anything without a body to lose them from. */
function visionOf(catalogue, combatant) {
    if (!combatant)
        return 1;
    const plan = catalogue.plan(combatant.loadout.planId);
    if (!plan)
        return 1;
    return deriveCapabilities(plan, combatant.vitals.body).sight;
}
/**
 * Can this viewer see that tile?
 *
 * Radius *and* line of sight, because either alone is a lie: a radius with no
 * occlusion sees through the hut wall, and occlusion with no radius sees the
 * length of the valley at midnight.
 *
 * A per-target check rather than a shadowcast of the whole field, and that is a
 * deliberate trade. The only thing this must be exactly right about is *what
 * goes on the wire*, which is a few dozen entities inside the area of interest
 * — a supercover walk each is cheaper than casting eight octants of a
 * 24-tile field ten times a second for every connected player. The client casts
 * its own field for the *look* of the dark, which it may do freely: it can only
 * darken things it was already sent.
 */
export function canSee(world, viewer, x, y, radius) {
    if (chebyshev(viewer.x, viewer.y, x, y) > radius)
        return false;
    return hasLineOfSight(world.map, viewer.x, viewer.y, x, y);
}
/* -------------------------------------------------------------------------- *
 * The weather system
 * -------------------------------------------------------------------------- */
/**
 * Roll the sky, once a game hour.
 *
 * On the engine's own `hour` phase rather than a modulo inside a `sim` system,
 * for the reason `floraSystem` is: the scheduler already knows how to run
 * something once a game hour, and a system that filtered ticks itself would be
 * a second answer to a settled question.
 */
export const weatherSystem = {
    name: 'weather',
    phase: 'hour',
    run(ctx) {
        const weather = ctx.world.weather;
        const was = advanceWeather(weather, ctx.rng('weather'), ctx.tick);
        if (weather.kind !== was) {
            ctx.emit({
                type: 'status',
                refs: ['weather', weather.kind, was],
                text: weatherText(weather.kind, weather.wind),
                // The sky is the one thing in Cwm Twrch that happens to everybody at
                // once, wherever they are standing.
                audience: 'world',
            });
        }
        // The light turning is narrated on the same beat, because both boundaries
        // land on a whole game hour and a player should get one line about the
        // evening rather than two.
        const phase = skyPhase(ctx.time);
        const before = skyPhase(gameTime(Math.max(0, ctx.tick - TICKS_PER_GAME_HOUR)));
        if (phase !== before) {
            ctx.emit({
                type: 'status',
                refs: ['sky', phase, before],
                text: phaseText(phase),
                audience: 'world',
            });
        }
    },
};
/* -------------------------------------------------------------------------- *
 * Lights
 * -------------------------------------------------------------------------- */
/**
 * Carry the lights and put out the spent ones, every tick.
 *
 * A `sim` system rather than an hourly one because it is doing two things a
 * game hour is far too coarse for: a carried brand has to be where its carrier
 * is *this* tick, or the light lags a player walking and the culling around
 * them lags with it.
 */
export const lightSystem = {
    name: 'lights',
    phase: 'sim',
    run(ctx) {
        const lights = ctx.world.lights;
        for (const id of Object.keys(lights).sort()) {
            const light = lights[id];
            if (!light)
                continue;
            if (light.carriedBy !== null) {
                const bearer = ctx.world.actors[light.carriedBy];
                if (bearer) {
                    light.x = bearer.x;
                    light.y = bearer.y;
                }
                else {
                    // Their hand is gone — they died, or logged out. The brand falls
                    // where they were standing and burns on, which is both true and the
                    // one bit of light a corpse leaves behind for whoever comes looking.
                    light.carriedBy = null;
                }
            }
            if (isBurning(light, ctx.tick))
                continue;
            delete lights[id];
            ctx.emit({
                type: 'status',
                actors: light.carriedBy === null ? [] : [light.carriedBy],
                refs: ['light', 'spent'],
                text: 'The brand gutters and goes out.',
                // Only the person holding it needs telling; from ten tiles away a light
                // going out is a thing you see, not a thing you are told.
                ...(light.carriedBy === null
                    ? { audience: 'witnesses', at: { x: light.x, y: light.y } }
                    : { audience: 'actor', to: light.carriedBy }),
            });
        }
    },
};
/* -------------------------------------------------------------------------- *
 * Striking a light
 * -------------------------------------------------------------------------- */
/** Why this actor cannot get a light going, or `null`. */
export function kindleFault(world, catalogue, actor, tick) {
    const combatant = world.combat[actor.id];
    if (combatant) {
        if (isHelpless(catalogue, combatant, tick))
            return 'you cannot';
        if (isCommitted(combatant, tick))
            return 'you are in the middle of something';
        if (isChannelling(combatant, tick))
            return 'you are already busy';
        // What working a flint asks of the hands (M3-04).
        const unable = capabilityFault(catalogue, combatant, 'kindle');
        if (unable)
            return unable;
    }
    if (carriedRadius(lightsOf(world), actor.id, tick) > 0) {
        return 'you already have a light';
    }
    if (fuelInHand(world, catalogue, actor.id) === null) {
        return 'you have nothing to burn';
    }
    return null;
}
/** The first thing in this satchel that will burn, or `null`. */
function fuelInHand(world, catalogue, actorId) {
    const inventory = world.inventories?.[actorId];
    if (!inventory)
        return null;
    for (const stack of inventory.items) {
        const def = catalogue.item?.(stack.defId);
        if (def?.tags.includes(FUEL_TAG))
            return def.id;
    }
    return null;
}
/**
 * Strike a light.
 *
 * Instant rather than channelled, and the brake is the *weather* instead of the
 * clock: a man crouched over his tinder for six seconds would be a man who
 * mostly gets interrupted, where a man whose tinder will not take in the rain
 * is a man learning the valley. The fuel is spent either way — that is what
 * makes failing in a downpour cost something.
 */
export const kindleHandler = {
    type: 'kindle',
    parse: parseKindle,
    check(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return 'you are not in the world';
        return kindleFault(ctx.world, ctx.catalogue, actor, ctx.tick);
    },
    apply(intent, ctx) {
        const actor = ctx.world.actors[intent.actorId];
        if (!actor)
            return;
        const fuel = fuelInHand(ctx.world, ctx.catalogue, actor.id);
        if (fuel === null)
            return;
        const inventory = ctx.world.inventories[actor.id];
        if (inventory)
            removeItem(inventory, fuel, 1);
        const weather = ctx.world.weather;
        const chance = kindleChance(weather);
        // Its own stream, so a wet night's failed lights cannot shift the dice the
        // weather itself is rolled from.
        if (!ctx.rng('fire').chance(chance)) {
            ctx.emit({
                type: 'interacted',
                actors: [actor.id],
                refs: ['kindle', 'failed', weather.kind],
                text: wetnessOf(weather) > 0.5
                    ? `${actor.name} works at the tinder, and it is too wet to take.`
                    : `${actor.name} works at the tinder, and it will not catch.`,
                audience: 'witnesses',
                at: { x: actor.x, y: actor.y },
            });
            return;
        }
        const lights = ctx.world.lights;
        // Numbered off the tick it was lit on, so the id is a function of world
        // state rather than of how many times anything has been called.
        const id = `light:${actor.id}:${ctx.tick}`;
        lights[id] = createLight(id, actor.x, actor.y, ctx.tick, {
            radius: TORCH_RADIUS,
            carriedBy: actor.id,
        });
        ctx.emit({
            type: 'interacted',
            actors: [actor.id],
            refs: ['kindle', 'lit', fuel],
            text: `${actor.name} strikes a light, and the dark backs off a little.`,
            audience: 'witnesses',
            at: { x: actor.x, y: actor.y },
        });
    },
};
/* -------------------------------------------------------------------------- *
 * Weather as a live input to M1-05
 * -------------------------------------------------------------------------- */
/**
 * The hourly infection roll, against the weather the valley is actually having.
 *
 * `infection.ts` has had the arithmetic since M1-05 and `CLEAN_CONDITIONS` /
 * `FOUL_CONDITIONS` were placeholders for a number that would one day move. This
 * is the day: the environment is derived from ground wetness, so a wound taken
 * in a week of rain is genuinely more likely to turn than the same wound taken
 * in a dry spell. Nothing about the risk model changed — only where the
 * multiplier comes from.
 */
export const infectionSystem = {
    name: 'infections',
    phase: 'hour',
    run(ctx) {
        const environment = infectionEnvironmentOf(weatherOf(ctx.world));
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant || !actor)
                continue;
            // Per-actor streams, so one man's fever cannot shift another's dice.
            for (const event of rollInfections(ctx.rng(`infection:${id}`), combatant.vitals, environment, ctx.tick)) {
                ctx.emit({
                    type: 'status',
                    actors: [id],
                    refs: [event.type, ...event.refs],
                    text: `${actor.name}'s wound is red and hot.`,
                    // Their own business until somebody unwraps the dressing (M4).
                    audience: 'actor',
                    to: id,
                });
            }
        }
    },
};
/**
 * The daily track: each infected wound checked against the body that has it.
 *
 * The other half of M1-05's clock, on the `day` phase where the ticket put it.
 * `care` was 0 until M4-03 gave somebody a way to help: a willow brew adds to
 * the toughness roll for as long as it lasts, which **buys time** in the fevered
 * stage rather than curing anything. That is the honest thing a bitter draught
 * can do for sepsis, and it is deliberately the only thing in the game that
 * touches this roll — the cure for gangrene is still a knife (M4-05).
 */
export const infectionTrackSystem = {
    name: 'infection-track',
    phase: 'day',
    run(ctx) {
        for (const id of Object.keys(ctx.world.combat).sort()) {
            const combatant = ctx.world.combat[id];
            const actor = ctx.world.actors[id];
            if (!combatant || !actor)
                continue;
            const needs = ctx.world.needs[id];
            const care = needs && isRelieved(needs, ctx.tick) ? needs.reliefFever : 0;
            for (const event of advanceInfection(ctx.rng(`infection:${id}`), combatant.vitals, combatant.loadout.attributes, care)) {
                ctx.emit({
                    type: event.type === 'died' ? 'died' : 'status',
                    actors: [id],
                    refs: [event.type, ...event.refs],
                    text: event.type === 'died'
                        ? `${actor.name} is dead.`
                        : `${actor.name} is flushed and shivering.`,
                    audience: event.type === 'died' ? 'witnesses' : 'actor',
                    ...(event.type === 'died' ? { at: { x: actor.x, y: actor.y } } : { to: id }),
                });
            }
        }
    },
};
/* -------------------------------------------------------------------------- *
 * The rule set
 * -------------------------------------------------------------------------- */
/** Everything M3-05 installs on the rule set. */
export const SKY_SYSTEMS = [
    lightSystem,
    weatherSystem,
    infectionSystem,
    infectionTrackSystem,
];
export const SKY_INTENTS = [kindleHandler];
//# sourceMappingURL=sky.js.map